import { AfterViewChecked, AfterViewInit, Component, ElementRef, EventEmitter, Input, Output, QueryList, ViewChildren } from '@angular/core';

import { PHONE_VERIFICATION_COPY } from '../../constants/credit-line.constants';

/** Largo del código de verificación del teléfono (ADM-813, lo fija `api`). */
export const PHONE_CODE_LENGTH = 6;

/**
 * ADM-813 — las 6 casillas del código de verificación del teléfono. Presentacional: no sabe de
 * HTTP ni de pasos. Adapta el patrón de `signup` (`verify-email.component.ts`: teclado, entrada de
 * autocompletado y pegado) sin importarlo. `(completed)` sale cada vez que las 6 quedan llenas;
 * `(changed)` en cada edición, para que el padre limpie el error. El padre lo reinicia con `reset()`.
 */
@Component({
    selector: 'fina-phone-code-input',
    templateUrl: './phone-code-input.component.html',
    styleUrls: ['./phone-code-input.component.scss'],
})
export class PhoneCodeInputComponent implements AfterViewInit, AfterViewChecked {
    @Input() disabled = false;
    /** Pinta las casillas en rojo (código incorrecto). */
    @Input() invalid = false;
    /** `aria-describedby` de las casillas (el mensaje de error del padre). */
    @Input() describedBy: string | null = null;

    @Output() completed = new EventEmitter<string>();
    @Output() changed = new EventEmitter<string>();

    @ViewChildren('box') boxes!: QueryList<ElementRef<HTMLInputElement>>;

    readonly copy = PHONE_VERIFICATION_COPY;
    readonly indexes = Array.from({ length: PHONE_CODE_LENGTH }, (_, i) => i);
    digits: string[] = Array(PHONE_CODE_LENGTH).fill('');
    /**
     * `reset()` llegó con las casillas deshabilitadas (el padre sigue `busy`): `focus()` sobre un input
     * deshabilitado no hace nada, así que se enfoca cuando la vista ya las muestra habilitadas (QA 001#2).
     */
    private focusPending = false;

    ngAfterViewInit(): void {
        this.focus(0);
    }

    ngAfterViewChecked(): void {
        if (!this.focusPending || this.disabled) return;
        this.focusPending = false;
        this.focus(0);
    }

    get value(): string {
        return this.digits.join('');
    }

    get isComplete(): boolean {
        return this.digits.every(d => /^\d$/.test(d));
    }

    /** Vacía las casillas y enfoca la primera (código nuevo o incorrecto). */
    reset(): void {
        this.digits = Array(PHONE_CODE_LENGTH).fill('');
        this.inputs().forEach(input => (input.value = ''));
        const first = this.inputs()[0];
        if (first && !first.disabled) this.focus(0);
        else this.focusPending = true;
    }

    onKeyDown(event: KeyboardEvent, index: number): void {
        const key = event.key;
        // Ctrl/Cmd + V (pegar), copiar, etc.: el navegador las maneja.
        if (event.ctrlKey || event.metaKey || event.altKey) return;
        if (key === 'Backspace') {
            event.preventDefault();
            if (this.digits[index]) {
                this.setDigit(index, '');
            } else if (index > 0) {
                this.setDigit(index - 1, '');
                this.focus(index - 1);
            }
            this.emitChange();
            return;
        }
        if (key === 'ArrowLeft' && index > 0) {
            event.preventDefault();
            this.focus(index - 1);
            return;
        }
        if (key === 'ArrowRight' && index < PHONE_CODE_LENGTH - 1) {
            event.preventDefault();
            this.focus(index + 1);
            return;
        }
        if (/^\d$/.test(key)) {
            event.preventDefault();
            this.setDigit(index, key);
            if (index < PHONE_CODE_LENGTH - 1) this.focus(index + 1);
            this.emitChange();
            return;
        }
        if (key !== 'Tab' && key !== 'Enter') event.preventDefault();
    }

    /** Entrada sin teclado físico (móvil) o autocompletado del SMS: uno o varios dígitos. */
    onInput(event: Event, index: number): void {
        const input = event.target as HTMLInputElement;
        const value = input.value || '';
        if (value === this.digits[index]) return;
        const onlyDigits = value.replace(/\D/g, '');
        // Teclado virtual de Android (`keydown` sin tecla): corregir una casilla ya escrita deja dos dígitos.
        // Se queda el nuevo; solo el pegado o el autocompletado llenan desde la primera (QA 001#3).
        const previous = this.digits[index];
        if (onlyDigits.length === 2 && previous && onlyDigits.includes(previous)) {
            this.setDigit(index, onlyDigits[0] === previous ? onlyDigits[1] : onlyDigits[0]);
            if (index < PHONE_CODE_LENGTH - 1) this.focus(index + 1);
            this.emitChange();
            return;
        }
        if (onlyDigits.length > 1) {
            this.fill(onlyDigits);
            return;
        }
        if (onlyDigits.length === 1) {
            this.setDigit(index, onlyDigits);
            if (index < PHONE_CODE_LENGTH - 1) this.focus(index + 1);
        } else {
            this.setDigit(index, '');
        }
        this.emitChange();
    }

    onPaste(event: ClipboardEvent): void {
        event.preventDefault();
        const digits = (event.clipboardData?.getData('text') || '').replace(/\D/g, '');
        if (digits) this.fill(digits);
    }

    /** Varios dígitos de una vez (pegado o autocompletado): llenan desde la primera casilla. */
    private fill(digits: string): void {
        const code = digits.slice(0, PHONE_CODE_LENGTH);
        for (let i = 0; i < PHONE_CODE_LENGTH; i++) this.setDigit(i, code[i] || '');
        const nextEmpty = this.digits.findIndex(d => !d);
        this.focus(nextEmpty >= 0 ? nextEmpty : PHONE_CODE_LENGTH - 1);
        this.emitChange();
    }

    private setDigit(index: number, digit: string): void {
        this.digits[index] = digit;
        const input = this.inputs()[index];
        if (input && input.value !== digit) input.value = digit;
    }

    private emitChange(): void {
        this.changed.emit(this.value);
        if (this.isComplete) this.completed.emit(this.value);
    }

    private focus(index: number): void {
        this.inputs()[index]?.focus();
    }

    private inputs(): HTMLInputElement[] {
        return this.boxes ? this.boxes.toArray().map(ref => ref.nativeElement) : [];
    }
}
